import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useSettingsQuery } from '@/hooks/queries/useSettingsQuery';
import { useClientsInfiniteQuery } from '@/features/clients/hooks/useClientsQuery';
import { useClientMutations } from '@/features/clients/hooks/useClientMutations';
import { ClientService } from '@/services/clientService';
import { queryKeys } from '@/hooks/queries/queryKeys';

export function useClientsDomain() {
    const queryClient = useQueryClient();
    const { isAuthenticated, isLoading: authLoading, user } = useAuth();
    const enabled = isAuthenticated && !authLoading;

    const settingsQuery = useSettingsQuery(enabled);
    const clientsQuery = useClientsInfiniteQuery(enabled);

    const authCompanyId = user?.app_metadata?.company_id as string | undefined;
    const computedCompanyId = settingsQuery.data?.id || authCompanyId || '';

    const clientMutations = useClientMutations(computedCompanyId);

    const searchClients = useCallback((query: string) => {
        return queryClient.fetchQuery({
            queryKey: [...queryKeys.clients(), 'search', query],
            queryFn: () => ClientService.searchClients(query),
            staleTime: 5 * 60 * 1000,
        });
    }, [queryClient]);

    return {
        // Data
        clients: clientsQuery.clients,
        
        // Actions
        loadMoreClients: clientsQuery.loadMoreClients,
        searchClients,
        
        // Loading States
        isLoading: clientsQuery.isLoading,
        isFetching: clientsQuery.isFetching,
        
        // Error state
        error: clientsQuery.error,

        // Mutations
        ...clientMutations
    };
}
